import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./Navbar.css";
import ThemeToggle from "./ThemeToggle";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [showPill, setShowPill] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [mobileNavHidden, setMobileNavHidden] = useState(false);

  const lastScrollY = useRef(0);
  const menuRef = useRef(null);
  const location = useLocation();

  const navItems = [
    { path: "/", label: "Home" },
    { path: "/services", label: "Services" },
    { path: "/about", label: "About Us" },
    { path: "/projects", label: "Projects" },
    { path: "/careers", label: "Careers" },
    { path: "/contact", label: "Contact Us" }
  ];

  /* Resize */
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 992);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* Scroll */
  useEffect(() => {
    const handleScroll = () => {
      const current = window.scrollY;
      setScrolled(current > 40);

      if (current > 100 && current > lastScrollY.current) {
        setShowPill(true);
        setMobileNavHidden(true);
      } else {
        setShowPill(false);
        setMobileNavHidden(false);
      }

      lastScrollY.current = current;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* Theme detection */
  useEffect(() => {
    const html = document.documentElement;

    const updateTheme = () => {
      setIsDarkTheme(html.getAttribute("data-theme") === "dark");
    };

    updateTheme();

    const observer = new MutationObserver(updateTheme);
    observer.observe(html, {
      attributes: true,
      attributeFilter: ["data-theme"]
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleTheme = () => {
    const html = document.documentElement;
    html.setAttribute("data-theme", isDarkTheme ? "light" : "dark");
  };

  return (
    <header className="navbar-wrapper">
      <AnimatePresence mode="wait">
        {showPill || isMobile ? (
          <motion.div
            key="pill"
            className="navbar-pill-container"
            initial={{ y: -60, opacity: 0 }}
            animate={{
              y: isMobile && mobileNavHidden ? -100 : 0,
              opacity: isMobile && mobileNavHidden ? 0 : 1
            }}
            exit={{ y: -60, opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="mobile-navbar-row" ref={menuRef}>

              {/* PILL */}
              <Link to="/contact" className="navbar-pill">
                <img
                  src={isDarkTheme ? "/logo-dark.png" : "/logo-light.png"}
                  alt="Deploy X"
                  className="pill-logo"
                  style={{
                    height: "50px",
                    width: "auto",
                    borderRadius: "50%",
                    objectFit: "contain"
                  }}
                />
                <span className="pill-text">Let's work</span>
                <div className="glowing-dot"></div>
              </Link>

              {/* RIGHT CONTROLS */}
              <div className="mobile-controls">
                <ThemeToggle />
                {isMobile && (
                  <div
                    className={`pill-hamburger ${menuOpen ? "open" : ""}`}
                    onClick={() => setMenuOpen(!menuOpen)}
                  >
                    <span></span>
                    <span></span>
                  </div>
                )}
              </div>

            </div>
          </motion.div>
        ) : (
          <motion.nav
            key="navbar"
            className={`navbar ${scrolled ? "navbar-scrolled" : ""}`}
            initial={{ y: -60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -60, opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="navbar-container">

              <Link to="/" className="navbar-logo">
                <img
                  src={isDarkTheme ? "/logo-dark.png" : "/logo-light.png"}
                  alt="Deploy X"
                />
              </Link>

              <ul className="nav-links">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={
                        location.pathname === item.path ? "active" : ""
                      }
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="nav-actions" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <Link to="/contact" className="nav-cta">
                  Get Started
                </Link>
                <ThemeToggle />
              </div>

            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* MOBILE DROPDOWN */}
      <AnimatePresence>
        {menuOpen && isMobile && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, y: -20, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: -20, x: "-50%" }}
          >
            <ul>
              {navItems.map((item, index) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setMenuOpen(false)}
                    className={
                      location.pathname === item.path ? "active" : ""
                    }
                  >
                    <span className="nav-number">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/contact"
                  className="mobile-nav-cta"
                  onClick={() => setMenuOpen(false)}
                >
                  Get Started
                </Link>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;